import React, { useState } from 'react';
import styled from 'styled-components';

export interface ContactPageProps {
  website: {
    salonInfo: any;
    content: {
      contactDescription?: string;
    };
  };
}

const ContactPage: React.FC<ContactPageProps> = ({ website }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    
    // Demo için form gönderme işlemi burada olacak
    // Gerçek uygulamada burada API çağrısı yapılacak
    setTimeout(() => {
      setFormStatus('success');
      // Form alanlarını temizle
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  // Website içeriğinden contactDescription özelliğini al veya varsayılan değeri kullan
  const contactDescription = website?.content?.contactDescription || 
    'Sorularınız, önerileriniz veya randevu talepleriniz için bizimle iletişime geçebilirsiniz';

  // Salon bilgilerini kontrol et
  if (!website || !website.salonInfo) {
    return (
      <ErrorContainer>
        <ErrorMessage>Salon bilgileri yüklenemedi.</ErrorMessage>
      </ErrorContainer>
    );
  }

  return (
    <ContactPageContainer>
      <PageHeader>
        <div className="container">
          <PageTitle>İletişim</PageTitle>
          <PageDescription>
            {contactDescription}
          </PageDescription>
        </div>
      </PageHeader>

      <ContactSection>
        <div className="container">
          <ContactContent>
            <ContactFormWrapper>
              <ContactForm onSubmit={handleSubmit}>
                <FormTitle>Bize Mesaj Gönderin</FormTitle>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="name">Adınız Soyadınız</FormLabel>
                    <FormInput 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                      placeholder="Adınız Soyadınız"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="email">E-posta Adresiniz</FormLabel>
                    <FormInput 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                      placeholder="E-posta Adresiniz"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="phone">Telefon Numaranız</FormLabel>
                    <FormInput 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Telefon Numaranız"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="subject">Konu</FormLabel>
                    <FormInput 
                      type="text" 
                      id="subject" 
                      name="subject" 
                      value={formData.subject}
                      onChange={handleChange}
                      required 
                      placeholder="Mesaj Konusu"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <FormLabel htmlFor="message">Mesajınız</FormLabel>
                  <FormTextarea 
                    id="message" 
                    name="message" 
                    rows={6} 
                    value={formData.message}
                    onChange={handleChange}
                    required 
                    placeholder="Mesajınızı buraya yazabilirsiniz..."
                  />
                </FormGroup>
                
                <SubmitButtonWrapper>
                  <SubmitButton 
                    type="submit" 
                    disabled={formStatus === 'sending'}
                  >
                    {formStatus === 'sending' ? 'Gönderiliyor...' : 'Mesajı Gönder'}
                  </SubmitButton>
                  
                  {formStatus === 'success' && (
                    <FormSuccessMessage>
                      <i className="material-icons">check_circle</i>
                      Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.
                    </FormSuccessMessage>
                  )}
                  
                  {formStatus === 'error' && (
                    <FormErrorMessage>
                      <i className="material-icons">error</i>
                      Mesajınız gönderilemedi. Lütfen daha sonra tekrar deneyin.
                    </FormErrorMessage>
                  )}
                </SubmitButtonWrapper>
              </ContactForm>
            </ContactFormWrapper>
            
            <ContactInfoWrapper>
              <ContactInfo>
                <ContactInfoTitle>İletişim Bilgilerimiz</ContactInfoTitle>
                
                <ContactInfoItem>
                  <ContactInfoIcon>
                    <i className="material-icons">location_on</i>
                  </ContactInfoIcon>
                  <ContactInfoText>
                    <strong>Adres</strong>
                    <p>{website.salonInfo.address}</p>
                  </ContactInfoText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactInfoIcon>
                    <i className="material-icons">phone</i>
                  </ContactInfoIcon>
                  <ContactInfoText>
                    <strong>Telefon</strong>
                    <p>
                      <a href={`tel:${website.salonInfo.phone?.replace(/[^0-9]/g, '')}`}>
                        {website.salonInfo.phone}
                      </a>
                    </p>
                  </ContactInfoText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactInfoIcon>
                    <i className="fab fa-whatsapp"></i>
                  </ContactInfoIcon>
                  <ContactInfoText>
                    <strong>WhatsApp</strong>
                    <p>
                      {(() => {
                        // Önce whatsapp numarasını alıyoruz, yoksa telefon numarasını kullanıyoruz
                        const whatsappNumber = website.salonInfo.whatsapp || website.salonInfo.phone;
                        
                        // Numarayı güvenli bir şekilde formatlıyoruz
                        let formattedNumber = '';
                        if (whatsappNumber) {
                          // Sadece rakamları alıyoruz
                          formattedNumber = whatsappNumber.replace(/[^0-9]/g, '');
                          
                          // Türkiye numarası ise ve başında 0 varsa kaldırıyoruz
                          if (formattedNumber.startsWith('0')) {
                            formattedNumber = formattedNumber.substring(1);
                          }
                          
                          // Başında ülke kodu yoksa 90 ekliyoruz (Türkiye)
                          if (!formattedNumber.startsWith('9') && formattedNumber.length === 10) {
                            formattedNumber = '90' + formattedNumber;
                          }
                        }
                        
                        return formattedNumber ? (
                          <a 
                            href={`https://wa.me/${formattedNumber}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {whatsappNumber}
                          </a>
                        ) : "WhatsApp bilgisi bulunamadı";
                      })()}
                    </p>
                  </ContactInfoText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactInfoIcon>
                    <i className="material-icons">schedule</i>
                  </ContactInfoIcon>
                  <ContactInfoText>
                    <strong>Çalışma Saatleri</strong>
                    <p>
                      {(() => {
                        const hours = website.salonInfo.workingHours;
                        
                        if (!hours) {
                          return (
                            <>
                              <span>Pazartesi - Cuma:</span> 09:00 - 19:00<br />
                              <span>Cumartesi:</span> 09:00 - 19:00<br />
                              <span>Pazar:</span> Kapalı
                            </>
                          );
                        }
                        
                        // API'den gelen günün açık/kapalı durumunu ve saatlerini gösteren yardımcı fonksiyon
                        const renderDay = (day: string, label: string) => {
                          const dayData = hours[day];
                          
                          if (!dayData) return null;
                          
                          // isOpen değeri false ise "Kapalı" göster
                          if (dayData.isOpen === false) {
                            return (
                              <>
                                <span>{label}:</span> Kapalı<br />
                              </>
                            );
                          }
                          
                          // isOpen true ise saatleri göster
                          if (dayData.isOpen === true) {
                            // Hours bir array ise ve içinde eleman varsa
                            if (Array.isArray(dayData.hours) && dayData.hours.length > 0) {
                              const timeSlots = dayData.hours.map((slot: any, index: number) => {
                                if (typeof slot === 'object' && slot.start && slot.end) {
                                  return `${slot.start} - ${slot.end}`;
                                }
                                return slot;
                              }).join(', ');
                              
                              return (
                                <>
                                  <span>{label}:</span> {timeSlots || '09:00 - 19:00'}<br />
                                </>
                              );
                            }
                            
                            // Saatler bilinmiyorsa varsayılan göster
                            return (
                              <>
                                <span>{label}:</span> 09:00 - 19:00<br />
                              </>
                            );
                          }
                          
                          return null;
                        };
                        
                        return (
                          <>
                            {renderDay('monday', 'Pazartesi')}
                            {renderDay('tuesday', 'Salı')}
                            {renderDay('wednesday', 'Çarşamba')}
                            {renderDay('thursday', 'Perşembe')}
                            {renderDay('friday', 'Cuma')}
                            {renderDay('saturday', 'Cumartesi')}
                            {renderDay('sunday', 'Pazar')}
                          </>
                        );
                      })()}
                    </p>
                  </ContactInfoText>
                </ContactInfoItem>
                
                <SocialLinks>
                  {website.salonInfo.instagram && (
                    <SocialLink 
                      href={website.salonInfo.instagram.startsWith('http') ? website.salonInfo.instagram : `https://instagram.com/${website.salonInfo.instagram}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-instagram"></i>
                    </SocialLink>
                  )}
                  {website.salonInfo.facebook && (
                    <SocialLink 
                      href={website.salonInfo.facebook.startsWith('http') ? website.salonInfo.facebook : `https://facebook.com/${website.salonInfo.facebook}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </SocialLink>
                  )}
                  {website.salonInfo.whatsapp && (
                    <SocialLink 
                      href={website.salonInfo.whatsapp.startsWith('http') ? website.salonInfo.whatsapp : `https://wa.me/${website.salonInfo.whatsapp.replace(/[^0-9]/g, '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-whatsapp"></i>
                    </SocialLink>
                  )}
                </SocialLinks>
              </ContactInfo>
            </ContactInfoWrapper>
          </ContactContent>
        </div>
      </ContactSection>

      {/* Harita Bölümü */}
      <MapSection>
        {(() => {
          // Koordinatları al
          let latitude = 0;
          let longitude = 0;
          
          // location objesi içinde lat/long değerleri varsa kullan
          if (website.salonInfo.location) {
            if (website.salonInfo.location.latitude && website.salonInfo.location.longitude) {
              latitude = website.salonInfo.location.latitude;
              longitude = website.salonInfo.location.longitude;
            }
          } 
          // Diğer formatta doğrudan latitude/longitude değerleri varsa kullan
          else if (website.salonInfo.latitude && website.salonInfo.longitude) {
            latitude = website.salonInfo.latitude;
            longitude = website.salonInfo.longitude;
          } 
          // Hiçbir koordinat yoksa varsayılan olarak İstanbul koordinatlarını kullan
          else {
            latitude = 41.0082;
            longitude = 28.9784;
          }
          
          // Harita için iframe URL oluştur
          const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
          
          return (
            <iframe 
              src={mapUrl}
              width="100%" 
              height="450" 
              style={{ border: 0 }}
              allowFullScreen={false} 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Salon Konumu"
            ></iframe>
          );
        })()}
      </MapSection>
    </ContactPageContainer>
  );
};

const ContactPageContainer = styled.div`
  margin-top: 80px;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}30, ${({ theme }) => theme.colors.secondary}30);
  padding: 80px 0 50px;
  text-align: center;
`;

const PageTitle = styled.h1`
  margin-bottom: 20px;
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  display: inline-block;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin: 15px auto 0;
    border-radius: 3px;
  }
`;

const PageDescription = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: 80px 0;
  background-color: #f9f9f9;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ContactFormWrapper = styled.div`
  transform: translateY(0);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ContactForm = styled.form`
  padding: 40px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h3`
  margin-bottom: 30px;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #444;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  background-color: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
  
  &::placeholder {
    color: #bbb;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  background-color: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}30;
  }
  
  &::placeholder {
    color: #bbb;
  }
`;

const SubmitButtonWrapper = styled.div`
  margin-top: 20px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  padding: 14px 30px;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 5px 15px ${({ theme }) => theme.colors.primary}40;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px ${({ theme }) => theme.colors.primary}60;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FormSuccessMessage = styled.div`
  margin-top: 15px;
  padding: 10px 15px;
  background-color: #e6f7e6;
  color: #2e7d32;
  border-radius: 5px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  
  i {
    margin-right: 8px;
  }
`;

const FormErrorMessage = styled.div`
  margin-top: 15px;
  padding: 10px 15px;
  background-color: #feecec;
  color: #c62828;
  border-radius: 5px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  
  i {
    margin-right: 8px;
  }
`;

const ContactInfoWrapper = styled.div`
  transform: translateY(0);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ContactInfo = styled.div`
  padding: 40px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  height: 100%;
`;

const ContactInfoTitle = styled.h2`
  margin-bottom: 30px;
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: rgba(255, 255, 255, 0.5);
    margin-top: 10px;
    border-radius: 3px;
  }
`;

const ContactInfoItem = styled.div`
  display: flex;
  margin-bottom: 25px;
`;

const ContactInfoIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 15px;
  flex-shrink: 0;
`;

const ContactInfoText = styled.div`
  color: white;
  line-height: 1.6;
  
  strong {
    font-weight: 600;
    margin-bottom: 5px;
    display: block;
    font-size: 1.1rem;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
  
  span {
    opacity: 0.8;
    font-weight: 500;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: opacity 0.3s;
    
    &:hover {
      opacity: 0.7;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 35px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 18px;
  transition: all 0.3s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-3px);
  }
`;

const MapSection = styled.section`
  height: 450px;
  width: 100%;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
  
  iframe {
    display: block;
    filter: grayscale(0.2);
    border-radius: 0;
    transition: filter 0.3s ease;
    
    &:hover {
      filter: grayscale(0);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  margin-top: 80px;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: red;
`;

export default ContactPage; 